import { Box, Grid, Tab, Tabs } from '@mui/material';
import useStyles from '../../style/js-style/components/AdminSettingsStyle';
import React, { useCallback, useContext, useState } from 'react';
import i18next from 'i18next';
import EATabPanel from '../common/EATabPanel';
import Attributes from '../../containers/administration/attributes/Attributes';
import Rules from '../../containers/administration/rules/Rules';
import { useQuery } from '../../config/Config';
import clsx from 'clsx';
import { Context } from '../../states/Store';
import { useHistory } from 'react-router';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from '../../services/common/RolesUtils';
import Calculator from '../../containers/administration/calculator/Calculator';
import Imports from 'containers/administration/imports/Imports/Imports';
import Demandes from 'containers/administration/demandes/Demandes';
import Theme from '../../containers/administration/theme/Theme';
import ContextConfiguration from '../../containers/administration/contexteModel';
import Consumption from '../../containers/administration/consumption/Consumption';
import { ADMIN_TABS_ID, getAdminSettingsBaseRoute } from './AdminSettingsUtils';
import { ADMIN_SECTION } from '../../services/common/Constants';
import HasNotPrivilegePage from 'components/HasNotPrivilegePage';

const a11yProps = (tabId) => {
  return {
    id: `${tabId}-tab`,
    'aria-controls': `${tabId}-tabpanel`,
  };
};

function AdminSettingTabs() {
  let tabQueryParam = useQuery().get('tab');
  const { hasAnyOfPrivileges } = React.useContext(
    RoleBasedAccessControlContext
  );
  const [value, setValue] = useState('attributes');
  const [state] = useContext(Context);
  const { classes } = useStyles();
  const history = useHistory();

  const getAdminTabs = useCallback(() => {
    return [
      {
        id: ADMIN_SECTION.DEMANDES,
        index: 0,
        label: i18next.t(`adminSettings.demandes`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE,
          PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
        ),
      },
      {
        id: ADMIN_SECTION.ATTRIBUTES,
        index: 1,
        label: i18next.t(`adminSettings.attributes`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE,
          PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE,
          PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE,
          PrivilegeEnum.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE
        ),
      },
      {
        id: ADMIN_SECTION.RULES,
        index: 2,
        label: i18next.t(`adminSettings.rules`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_RULE
        ),
      },
      {
        id: ADMIN_SECTION.CALCULATOR,
        index: 3,
        label: i18next.t(`adminSettings.calculator`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_CALCULATION
        ),
      },
      {
        id: ADMIN_SECTION.CONTEXT,
        index: 4,
        label: i18next.t(`adminSettings.contexte`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_MODEL
        ),
      },
      {
        id: ADMIN_SECTION.IMPORT,
        index: 5,
        label: i18next.t(`adminSettings.import`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_IMPORTS_AND_UPDATES
        ),
      },
      {
        id: ADMIN_SECTION.PERSONALIZATION,
        index: 6,
        label: i18next.t(`adminSettings.personalization`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_THEME
        ),
      },
      {
        id: ADMIN_SECTION.CONSUMPTION,
        index: 7,
        label: i18next.t(`adminSettings.consumption`),
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_CONSUMPTION
        ),
      },
    ];

  }, [hasAnyOfPrivileges]);

  const handleChange = (event, newValue) => {
    const newId = event.target.id.substring(0, event.target.id.length - 4);
    setValue(newId);
    history.push(
      `/${state.account.id}/${getAdminSettingsBaseRoute()}?tab=${newId}`
    );
  };

  React.useEffect(() => {
    if (tabQueryParam) {
      const filteredTabs = getAdminTabs().filter((tab) => tab.id === tabQueryParam);

      if (filteredTabs?.[0]?.isAuthorized) {
        setValue(tabQueryParam);
      } else if(filteredTabs.length === 0) {
        //case where tab is wrong
        history.push("/");
      }
    } else {
      history.push("/");
    }
  }, [tabQueryParam, getAdminTabs, history]);

  const renderTabPanelContent = (tabId) => {
     if(!getAdminTabs().filter((tab) => tab.id === tabQueryParam)?.[0]?.isAuthorized) {
      return <HasNotPrivilegePage />;
    }
    switch (tabId) {
      case ADMIN_SECTION.DEMANDES:
        return <Demandes />;

      case ADMIN_SECTION.ATTRIBUTES:
        return <Attributes />;

      case ADMIN_SECTION.RULES:
        return <Rules />;

      case ADMIN_SECTION.CALCULATOR:
        return <Calculator />;

      case ADMIN_SECTION.IMPORT:
        return <Imports />;

      case ADMIN_SECTION.PERSONALIZATION:
        return <Theme />;

      case ADMIN_SECTION.CONSUMPTION:
        return <Consumption />;

      case ADMIN_SECTION.CONTEXT:
        return <ContextConfiguration />;

      default:
        return <></>;
    }
  }

  return (
    <>
      <Grid item xs={12} style={{ padding: 0, position: "relative" }}>
        {/* Tabs */}
        <Box className={classes.tabsWrapper}>
          <Tabs
            id={ADMIN_TABS_ID}
            aria-label="admin tabs"
            value={value}
            onChange={handleChange}
          >
            {getAdminTabs().map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.label}
                {...a11yProps(tab.id)}
                disabled={!tab.isAuthorized}
                className={clsx(
                  classes.tab,
                  !tab.isAuthorized
                    ? classes.tabDisabled
                    : value === tab.id
                    ? classes.selectedTab
                    : classes.unselectedTab
                )}
              />
            ))}
          </Tabs>
        </Box>

        {getAdminTabs().map((tab) => (
          <EATabPanel
            key={tab.id}
            value={value}
            index={tab.id}
            className={classes.tabContentWrapper}
            style={{ position: 'relative' }}
          >
            {renderTabPanelContent(tab.id)}
          </EATabPanel>
        ))}
      </Grid>
    </>
  );
}

export default AdminSettingTabs;
